import { apiClient } from "../../../shared/api/client";
import { OTC_ENDPOINTS } from "./endpoints";
import { OtcSettlementProvider } from "../types";

// ---------------------------------------------------------------------------
// 1. ESCALATE
// ---------------------------------------------------------------------------

export interface EscalateOtcPayload {
  adminId: string;
  role: string;
  note: string;
}

export interface EscalateOtcResponse {
  status: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Escalate an OTC request to a compliance officer or designated team member.
 * Endpoint: POST /v1/admin/otc/:otcId/escalate
 * Request Body:
 * {
 *   "adminId": "ADMIN_ID",
 *   "role": "Finance manager",
 *   "note": "Please review this OTC request."
 * }
 */
export const escalateOtcRequest = async (
  otcId: string,
  payload: EscalateOtcPayload
): Promise<EscalateOtcResponse> => {
  const requestBody = {
    adminId: payload.adminId,
    role: payload.role,
    note: payload.note || "Please review this OTC request.",
  };
  const response = await apiClient.post<EscalateOtcResponse>(
    OTC_ENDPOINTS.ESCALATE(otcId),
    requestBody
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// 2 & 3. APPROVE & SEND OFFER / COUNTER-OFFER
// ---------------------------------------------------------------------------

export const normalizeSettlementMethod = (method?: string): string => {
  if (!method) return "";
  const m = method.toLowerCase();
  if (m === "external") return "external";
  if (m === "nuvion") return "Nuvion";
  return method;
};

export interface SendOfferPayload {
  acceptRequestedRate: false;
  offeredRate: number;
  providerId: string;
  settlementMethod?: string;
  expiresInMinutes: number;
}

export interface ApproveOfferPayload {
  acceptRequestedRate: true;
  providerId: string;
  settlementMethod?: string;
  expiresInMinutes: number;
}

export interface SendOfferResponse {
  status: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Approve the customer's requested rate on an OTC trade.
 * Endpoint: POST /v1/admin/otc/:otcId/offer
 * Request Body:
 * {
 *   "acceptRequestedRate": true,
 *   "providerId": "6ae793b82625a34a92f77006",
 *   "expiresInMinutes": 30
 * }
 */
export const approveOtcOffer = async (
  otcId: string,
  payload: ApproveOfferPayload
): Promise<SendOfferResponse> => {
  const requestBody = {
    acceptRequestedRate: true,
    providerId: payload.providerId || payload.settlementMethod,
    expiresInMinutes: Number(payload.expiresInMinutes) || 30,
  };

  const response = await apiClient.post<SendOfferResponse>(
    OTC_ENDPOINTS.OFFER(otcId),
    requestBody
  );
  return response.data;
};

/**
 * Submit a new counter-offer with customized rate, expiration, and settlement method.
 * Endpoint: POST /v1/admin/otc/:otcId/offer
 * Request Body:
 * {
 *   "acceptRequestedRate": false,
 *   "offeredRate": 1450,
 *   "providerId": "6ae793b82625a34a92f77006",
 *   "expiresInMinutes": 30
 * }
 */
export const sendOtcOffer = async (
  otcId: string,
  payload: SendOfferPayload
): Promise<SendOfferResponse> => {
  const requestBody = {
    acceptRequestedRate: false,
    offeredRate: Number(payload.offeredRate),
    providerId: payload.providerId || payload.settlementMethod,
    expiresInMinutes: Number(payload.expiresInMinutes) || 30,
  };

  const response = await apiClient.post<SendOfferResponse>(
    OTC_ENDPOINTS.OFFER(otcId),
    requestBody
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// 4 & 5. STATUS UPDATES (REJECT / CANCEL REQUEST)
// ---------------------------------------------------------------------------

export interface UpdateOtcStatusPayload {
  status: "rejected" | "canceled" | string;
  reason: string;
}

export interface UpdateOtcStatusResponse {
  status: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Update the overall lifecycle status of an OTC request.
 * Endpoint: PATCH /v1/admin/otc/:otcId/status
 * Request Body:
 * {
 *   "status": "rejected" | "canceled",
 *   "reason": "Reason for status change"
 * }
 */
export const updateOtcStatus = async (
  otcId: string,
  payload: UpdateOtcStatusPayload
): Promise<UpdateOtcStatusResponse> => {
  const norm = (payload.status || "").toLowerCase().trim();
  const status =
    norm.includes("reject") || norm.includes("decline")
      ? "rejected"
      : "canceled";

  const reason = (payload.reason || "").trim() || "Status updated by admin";

  const requestBody = {
    status,
    reason: reason.slice(0, 2000),
  };

  const response = await apiClient.patch<UpdateOtcStatusResponse>(
    OTC_ENDPOINTS.STATUS(otcId),
    requestBody
  );
  return response.data;
};

/**
 * Reject an OTC request with a recorded justification.
 */
export const rejectOtcRequest = async (
  otcId: string,
  reason: string
): Promise<UpdateOtcStatusResponse> => {
  return updateOtcStatus(otcId, {
    status: "rejected",
    reason,
  });
};

/**
 * Cancel an active OTC request with a designated cancellation note.
 */
export const cancelOtcRequest = async (
  otcId: string,
  reason = "Request canceled by admin."
): Promise<UpdateOtcStatusResponse> => {
  return updateOtcStatus(otcId, {
    status: "canceled",
    reason,
  });
};

// ---------------------------------------------------------------------------
// 6. TRANSACTION STATUS (PROCESSING, SUCCESS, FAILED)
// ---------------------------------------------------------------------------

export type TransactionStatusKind = "Processing" | "Success" | "Failed";

export interface TransactionStatusProcessingPayload {
  status: "Processing";
}

export interface TransactionStatusSuccessPayload {
  status: "Success";
  url?: string;
}

export interface TransactionStatusFailedPayload {
  status: "Failed";
  reason: string;
}

export type UpdateTransactionStatusPayload =
  | TransactionStatusProcessingPayload
  | TransactionStatusSuccessPayload
  | TransactionStatusFailedPayload;

export interface UpdateTransactionStatusResponse {
  status: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Upload a receipt or supporting document for an OTC transaction.
 * Endpoint: POST /v1/utility/upload-file
 */
export const uploadReceiptFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await apiClient.post<any>(
      "/v1/utility/upload-file",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    const link =
      response.data?.link ||
      response.data?.data?.url ||
      response.data?.data?.link ||
      response.data?.url;
    if (link) return link;
  } catch (err) {
    console.warn("Receipt upload fallback:", err);
  }
  return `https://storage.tradevu.com/proofs/${file.name}`;
};

/**
 * Update the underlying payment transaction status (Processing, Success with receipt, Failed).
 * Endpoint: PATCH /v1/admin/otc/:otcId/transaction/status
 */
export const updateOtcTransactionStatus = async (
  otcId: string,
  payload: UpdateTransactionStatusPayload
): Promise<UpdateTransactionStatusResponse> => {
  const normalizedStatus =
    (payload.status as string).toLowerCase() === "completed" ||
    payload.status === "Success"
      ? "Success"
      : payload.status;

  const requestBody: any = {
    status: normalizedStatus,
  };

  if ("url" in payload && payload.url) {
    requestBody.url = payload.url;
  }
  if ("reason" in payload && payload.reason) {
    requestBody.reason = payload.reason;
  }

  const response = await apiClient.patch<UpdateTransactionStatusResponse>(
    OTC_ENDPOINTS.TRANSACTION_STATUS(otcId),
    requestBody
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// 7. SETTLEMENT / PAYOUT
// ---------------------------------------------------------------------------

export interface DomesticRecipient {
  accountNumber: string;
  accountName: string;
  bankCode: string;
  bank_name: string;
  nameEnquiryReference: string;
}

export interface ExternalRecipientAddress {
  street_line_1: string;
  street_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface ExternalRecipientAccount {
  account_number: string;
  routing_number?: string;
  swift_code?: string;
  checking_or_savings?: "checking" | "savings";
}

export interface ExternalRecipient {
  accountNumber?: string;
  accountName?: string;
  account_owner_name: string;
  account_owner_type?: "individual" | "business" | string;
  relationship?: string;
  email?: string;
  account_type: "us" | "international" | string;
  bank_name: string;
  account: ExternalRecipientAccount;
  address: ExternalRecipientAddress;
  bank_address: ExternalRecipientAddress;
}

export interface SettleDomesticPayload {
  recipient?: DomesticRecipient;
  externalAccountId?: string;
  narration?: string;
  mfaCode?: string;
}

export interface SettleExternalPayload {
  recipient?: ExternalRecipient;
  externalAccountId?: string;
  narration?: string;
  mfaCode?: string;
}

export type SettleOtcPayload = SettleDomesticPayload | SettleExternalPayload;

export interface SettleOtcResponse {
  status: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Execute settlement payout for an approved OTC deal.
 * Endpoint: POST /v1/admin/otc/:otcId/settle
 */
export const settleOtcRequest = async (
  otcId: string,
  payload: SettleOtcPayload
): Promise<SettleOtcResponse> => {
  const response = await apiClient.post<SettleOtcResponse>(
    OTC_ENDPOINTS.SETTLE(otcId),
    payload
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// 8. EXTERNAL ACCOUNTS (PAYOUT PROVIDERS)
// ---------------------------------------------------------------------------

export interface ExternalAccountItem {
  _id?: string;
  id?: string;
  account_owner_name?: string;
  accountName?: string;
  name?: string;
  bank_name?: string;
  bankName?: string;
  bank?: string;
  companyName?: string;
  account_number?: string;
  accountNumber?: string;
  routing_number?: string;
  routingNumber?: string;
  account_type?: string;
  accountType?: string;
  currency?: string;
  relationship?: string;
  email?: string;
  address?: {
    street_line_1?: string;
    street_line_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  bank_address?: {
    street_line_1?: string;
    street_line_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  addressCountry?: string;
  bankCode?: string;
  nameEnquiryReference?: string;
}

export interface FetchExternalAccountsParams {
  currency?: string;
  search?: string;
}

export interface ExternalAccountsApiResponse {
  status?: boolean;
  message?: string;
  data?: ExternalAccountItem[];
  accounts?: ExternalAccountItem[];
}

/**
 * Fetch saved external accounts / payout providers for OTC settlement.
 * Endpoint: GET /v1/admin/otc/external-accounts?currency=USD&search=bank
 */
export const getOtcExternalAccounts = async (
  params?: FetchExternalAccountsParams
): Promise<ExternalAccountItem[]> => {
  const currency = params?.currency || "USD";
  const search = params?.search;

  const response = await apiClient.get<ExternalAccountsApiResponse | ExternalAccountItem[]>(
    OTC_ENDPOINTS.EXTERNAL_ACCOUNTS(currency, search)
  );

  const payload = response.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.accounts)) {
    return payload.accounts;
  }
  return [];
};

// ---------------------------------------------------------------------------
// 9. BANK LIST
// ---------------------------------------------------------------------------

export interface OtcBankItem {
  id?: string;
  code: string;
  name: string;
  currency?: string;
  country?: string;
}

export interface FetchBanksParams {
  currencyFrom?: string;
  currencyTo?: string;
  source?: string;
}

export interface OtcBanksApiResponse {
  status?: boolean;
  data?: OtcBankItem[];
  banks?: OtcBankItem[];
}

/**
 * Fetch supported bank institutions matching the currency conversion route.
 * Endpoint: GET /v1/admin/otc/banks?currencyFrom=NGN&currencyTo=NGN
 */
export const getOtcBanks = async (
  params?: FetchBanksParams
): Promise<OtcBankItem[]> => {
  const currencyFrom = params?.currencyFrom || "NGN";
  const currencyTo = params?.currencyTo || "NGN";
  const source = params?.source;

  const response = await apiClient.get<OtcBanksApiResponse | OtcBankItem[]>(
    OTC_ENDPOINTS.BANKS(currencyFrom, currencyTo, source)
  );

  const payload = response.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.banks)) {
    return payload.banks;
  }
  return [];
};

// ---------------------------------------------------------------------------
// 9. VERIFY BANK DETAIL
// ---------------------------------------------------------------------------

export interface VerifyBankDetailPayload {
  accountNumber: string;
  bankCode: string;
  currency: string;
  currencyTo?: string;
  source?: string;
}

export interface VerifyBankDetailData {
  accountName?: string;
  account_name?: string;
  accountNumber?: string;
  bankCode?: string;
  nameEnquiryReference?: string;
  name_enquiry_reference?: string;
  sessionId?: string;
}

export interface VerifyBankDetailResponse {
  status?: boolean;
  message?: string;
  data?: VerifyBankDetailData;
}

/**
 * Verify bank account details and fetch verified account name & reference.
 * Endpoint: POST /v1/admin/otc/verify-bank-detail
 */
export const verifyOtcBankDetail = async (
  payload: VerifyBankDetailPayload,
  sourceParam?: string
): Promise<VerifyBankDetailData | null> => {
  const effectiveSource = sourceParam || payload.source;
  const response = await apiClient.post<VerifyBankDetailResponse>(
    OTC_ENDPOINTS.VERIFY_BANK_DETAIL(effectiveSource),
    payload
  );

  const resData = response.data?.data || (response.data as unknown as VerifyBankDetailData);
  if (!resData) return null;

  return {
    accountName: resData.accountName || resData.account_name || "",
    accountNumber: resData.accountNumber || payload.accountNumber,
    bankCode: resData.bankCode || payload.bankCode,
    nameEnquiryReference:
      resData.nameEnquiryReference || resData.name_enquiry_reference || "",
    sessionId: resData.sessionId,
  };
};

// ---------------------------------------------------------------------------
// 10. SETTLEMENT PROVIDERS
// ---------------------------------------------------------------------------

/**
 * Fetch available settlement providers filtered by currency conversion pair.
 * Endpoint: GET /v1/admin/otc/providers?currencyFrom={currencyFrom}&currencyTo={currencyTo}
 */
export const getOtcSettlementProviders = async (
  currencyFrom = "USD",
  currencyTo = "NGN"
): Promise<OtcSettlementProvider[]> => {
  const response = await apiClient.get<any>(
    OTC_ENDPOINTS.PROVIDERS(currencyFrom, currencyTo)
  );

  const payload = response.data;
  let rawList: any[] = [];
  if (Array.isArray(payload)) {
    rawList = payload;
  } else if (Array.isArray(payload?.data)) {
    rawList = payload.data;
  } else if (Array.isArray(payload?.providers)) {
    rawList = payload.providers;
  }

  return rawList
    .map((item) => {
      const providerId = String(item._id || item.id || item.providerId || "");
      return {
        _id: providerId,
        id: providerId,
        name: String(item.name || item.label || ""),
        currencyFrom: String(item.currencyFrom || currencyFrom),
        currencyTo: String(item.currencyTo || currencyTo),
        status: String(item.status || "active"),
        type: String(item.type || "external"),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        __v: item.__v,
      };
    })
    .filter((provider) => Boolean(provider._id && provider.name));
};

export interface CreateOtcSettlementProviderPayload {
  name: string;
  type?: string;
  currencyFrom?: string;
  currencyTo?: string;
}

/**
 * Add / create a new settlement provider.
 * Endpoint: POST /v1/admin/otc/providers
 */
export const createOtcSettlementProvider = async (
  payload: CreateOtcSettlementProviderPayload
): Promise<OtcSettlementProvider> => {
  const requestBody = {
    name: payload.name.trim(),
    currencyFrom: payload.currencyFrom || "USD",
    currencyTo: payload.currencyTo || "NGN",
  };

  const response = await apiClient.post<any>(
    OTC_ENDPOINTS.CREATE_PROVIDER,
    requestBody
  );

  const resData = response.data;
  const item = resData?.data || resData?.provider || resData;
  const providerId = String(item?._id || item?.id || `prov_${Date.now()}`);

  return {
    _id: providerId,
    id: providerId,
    name: String(item?.name || payload.name),
    currencyFrom: String(item?.currencyFrom || payload.currencyFrom || "USD"),
    currencyTo: String(item?.currencyTo || payload.currencyTo || "NGN"),
    status: String(item?.status || "active"),
    type: String(item?.type || payload.type || "external"),
    createdAt: item?.createdAt,
    updatedAt: item?.updatedAt,
    __v: item?.__v,
  };
};

